import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Calendar, Mail, MapPin, MessageCircle, Loader2, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { submitLead } from "../lib/api";
import { Turnstile } from "../components/Turnstile";
import { whatsappLink } from "../lib/whatsapp";

export default function ContactPage() {
  const { t, locale } = useI18n();
  useSeo({
    title: locale === "de"
      ? "Kontakt — Anfrage senden | Joshi FusionTech Media Berlin"
      : "Contact — Send an enquiry | Joshi FusionTech Media Berlin",
    description: locale === "de"
      ? "Schreib uns eine Nachricht oder buche ein 30-Minuten-Strategiegespräch. KI-Automatisierung, Webentwicklung & Marketing aus Berlin."
      : "Send us a message or book a 30-min strategy call. AI automation, web development & marketing from Berlin.",
    canonical: "https://www.joshifusiontech.de/contact",
    locale,
  });
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", service: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    if (!consent) {
      setStatus("error");
      setErrorMsg(t.contact.consentRequired);
      return;
    }
    if (!turnstileToken) {
      setStatus("error");
      setErrorMsg(t.contact.captchaError);
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      await submitLead({
        ...form,
        locale,
        consent: true,
        turnstile_token: turnstileToken,
      });
      setStatus("success");
      setForm({ name: "", email: "", company: "", phone: "", service: "", message: "" });
      setConsent(false);
      setTurnstileToken("");
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch (err) { console.warn("Contact: turnstile reset failed", err); }
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || t.contact.error);
    }
  };

  const services = [
    { v: "ai", label: t.services.ai.title },
    { v: "web", label: t.services.web.title },
    { v: "marketing", label: t.services.marketing.title },
    { v: "consulting", label: t.services.consulting.title },
    { v: "other", label: "Other" },
  ];

  return (
    <main data-testid="contact-page" className="pt-32">
      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{t.contact.kicker}</span>
          </div>
          <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl tracking-tighter text-white">
            {t.contact.title}
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">{t.contact.subtitle}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form */}
          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 glass rounded-3xl p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={t.contact.name} required>
                <input
                  data-testid="contact-name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="input-field"
                />
              </Field>
              <Field label={t.contact.email} required>
                <input
                  data-testid="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="input-field"
                />
              </Field>
              <Field label={t.contact.company}>
                <input data-testid="contact-company" value={form.company} onChange={update("company")} className="input-field" />
              </Field>
              <Field label={t.contact.phone}>
                <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="input-field" />
              </Field>
              <Field label={t.contact.service} className="md:col-span-2">
                <select
                  data-testid="contact-service"
                  value={form.service}
                  onChange={update("service")}
                  className="input-field"
                >
                  <option value="">{t.contact.pickService}</option>
                  {services.map((s) => (
                    <option key={s.v} value={s.v}>{s.label}</option>
                  ))}
                </select>
              </Field>
              <Field label={t.contact.message} required className="md:col-span-2">
                <textarea
                  data-testid="contact-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="input-field resize-none"
                />
              </Field>
            </div>

            {/* GDPR consent + Cloudflare Turnstile */}
            <div className="mt-8 space-y-5">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-label">
                <input
                  type="checkbox"
                  data-testid="contact-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 rounded border-white/20 bg-white/[0.04] accent-brand-orange shrink-0 cursor-pointer"
                />
                <span className="text-sm text-white/70 leading-relaxed">
                  {t.contact.consentLabel}{" "}
                  <Link
                    to="/legal/datenschutz"
                    className="text-brand-orange underline underline-offset-2 hover:text-brand-orangeSoft"
                    data-testid="consent-privacy-link"
                  >
                    {t.contact.consentLink}
                  </Link>
                  {t.contact.consentSuffix ? ` ${t.contact.consentSuffix}` : "."}
                </span>
              </label>

              <div>
                <div className="label-mono mb-2">{t.contact.captchaLabel}</div>
                <Turnstile
                  onToken={(token) => setTurnstileToken(token || "")}
                  testId="contact-turnstile"
                />
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status === "sending" || !consent || !turnstileToken}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "sending" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t.contact.sending}</>
                ) : (
                  <><Send className="w-4 h-4" /> {t.contact.send}</>
                )}
              </button>
              {status === "success" && (
                <span data-testid="contact-success" className="flex items-center gap-2 text-sm text-emerald-400">
                  <Check className="w-4 h-4" /> {t.contact.success}
                </span>
              )}
              {status === "error" && (
                <span data-testid="contact-error" className="text-sm text-red-400">{errorMsg}</span>
              )}
            </div>
          </form>

          {/* Sidebar */}
          <aside className="lg:col-span-5 flex flex-col gap-6">
            <Link
              to="/book"
              data-testid="calendly-cta"
              className="glass rounded-3xl p-8 hover:border-brand-orange/40 transition-all group block"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-azure/20 border border-brand-azure/40 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-brand-azureSoft" />
                </div>
                <div>
                  <div className="font-display text-xl text-white">{t.contact.bookCall}</div>
                  <div className="text-sm text-white/50 font-mono">30 min · free</div>
                </div>
              </div>
            </Link>

            <div className="glass rounded-3xl p-8 space-y-4">
              <a
                href="mailto:info@joshifusiontech.de"
                className="flex items-center gap-3 text-sm text-white/80 hover:text-white transition-colors"
                data-testid="email-info"
              >
                <Mail className="w-4 h-4 text-brand-orange shrink-0" />
                <span className="contact-email">info@joshifusiontech.de</span>
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-center gap-3 text-sm text-white/80 hover:text-white transition-colors"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366] shrink-0" />
                <span>WhatsApp Business</span>
              </a>
              <div className="flex items-center gap-3 text-sm text-white/70">
                <MapPin className="w-4 h-4 text-brand-orange shrink-0" />
                <span>Berlin, Germany</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <style>{`
        .input-field {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          color: #FAFAFF;
          padding: 12px 14px;
          font-size: 14px;
          transition: border-color 0.2s, background 0.2s;
        }
        .input-field:focus { outline: none; border-color: #0066FF; background: rgba(255,255,255,0.05); }
        .input-field::placeholder { color: rgba(255,255,255,0.35); }
        select.input-field { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A0A0B0' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 40px; }
        select.input-field option { background: #101018; color: #FAFAFF; }
      `}</style>
    </main>
  );
}

const Field = ({ label, required, className = "", children }) => (
  <label className={`block ${className}`}>
    <div className="label-mono mb-2">
      {label} {required && <span className="text-brand-orange">*</span>}
    </div>
    {children}
  </label>
);
